export const TEMPORAL_PRECISIONS = ['minute', 'second', 'millisecond'] as const
const TEMPORAL_PRECISION_NAMES: readonly string[] = TEMPORAL_PRECISIONS

export type TemporalPrecision = (typeof TEMPORAL_PRECISIONS)[number]
export type TemporalPrecisionConfig = TemporalPrecision | number

export type DateTimeParts = Readonly<{
  date: string
  time: string
}>

export type PreservedMilliseconds = Readonly<{
  milliseconds: string
  value: string
}>

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const TIME_PATTERN = /^(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/
const VALID_TIME_PATTERN =
  /^(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d{3})?)?$/

const EMPTY_DATE_TIME_PARTS: DateTimeParts = { date: '', time: '' }

export const isTemporalPrecision = (
  value: unknown
): value is TemporalPrecision =>
  typeof value === 'string' && TEMPORAL_PRECISION_NAMES.includes(value)

export const temporalPrecision = (
  value: unknown,
  fallback: TemporalPrecision = 'minute'
): TemporalPrecision => (isTemporalPrecision(value) ? value : fallback)

export const normalizeTime = (
  value: string,
  precision: TemporalPrecision
): string | null => {
  const match = TIME_PATTERN.exec(value)

  if (!match) {
    return null
  }

  const hour = match[1]
  const minute = match[2]
  const second = match[3] ?? '00'
  const millisecond = (match[4] ?? '').padEnd(3, '0')

  if (Number(hour) >= 24 || Number(minute) >= 60 || Number(second) >= 60) {
    return null
  }

  if (precision === 'minute') {
    return `${hour}:${minute}`
  }

  if (precision === 'second') {
    return `${hour}:${minute}:${second}`
  }

  return `${hour}:${minute}:${second}.${millisecond || '000'}`
}

export const preserveTimeMilliseconds = (
  value: string,
  previousMilliseconds = '000'
): PreservedMilliseconds => {
  if (value === '') {
    return { milliseconds: '000', value: '' }
  }

  const match = TIME_PATTERN.exec(value)
  const milliseconds = match?.[4]
    ? match[4].padEnd(3, '0').slice(0, 3)
    : previousMilliseconds
  const seconds = normalizeTime(value, 'second')

  return {
    milliseconds,
    value: seconds ? `${seconds}.${milliseconds}` : value
  }
}

export const splitDateTimeValue = (
  value: string,
  precision: TemporalPrecision
): DateTimeParts => {
  const [date = '', time = '', ...remainder] = value.split('T')
  const normalizedTime = normalizeTime(time, precision)

  return remainder.length === 0 && hasValidDateShape(date) && normalizedTime
    ? { date, time: normalizedTime }
    : EMPTY_DATE_TIME_PARTS
}

export const combineDateTimeValue = (
  date: string,
  time: string,
  precision: TemporalPrecision
): string => {
  const normalizedTime = normalizeTime(time, precision)

  return hasValidDateShape(date) && normalizedTime
    ? `${date}T${normalizedTime}`
    : ''
}

export const datePart = (value: string | undefined): string =>
  value?.split('T')[0] ?? ''

export const timePart = (value: string | undefined): string =>
  value?.split('T')[1] ?? value ?? ''

export const matchingDateTimeBound = (
  date: string,
  value: string | undefined,
  precision?: TemporalPrecision
): string => {
  const [boundDate = '', boundTime = '', ...remainder] = value?.split('T') ?? []
  const normalizedTime = normalizeTime(
    boundTime,
    precision ?? inferTemporalPrecision(boundTime)
  )

  return remainder.length === 0 &&
    date === boundDate &&
    hasValidDateShape(boundDate) &&
    normalizedTime
    ? normalizedTime
    : ''
}

export const inferTemporalPrecision = (
  value: string,
  configured?: TemporalPrecisionConfig
): TemporalPrecision => {
  if (
    configured === 'millisecond' ||
    (typeof configured === 'number' && configured < 1)
  ) {
    return 'millisecond'
  }

  if (
    configured === 'second' ||
    (typeof configured === 'number' && configured < 60) ||
    value.includes(':', 3)
  ) {
    return value.includes('.') ? 'millisecond' : 'second'
  }

  return 'minute'
}

export const temporalPrecisionStep = (precision: TemporalPrecision): number =>
  precision === 'minute' ? 60 : precision === 'second' ? 1 : 0.001

export const configuredTemporalStep = (
  configured: TemporalPrecisionConfig | undefined,
  inferredPrecision: TemporalPrecision
): number =>
  typeof configured === 'number'
    ? configured
    : temporalPrecisionStep(inferredPrecision)

export const hasValidDateShape = (value: string): boolean =>
  DATE_PATTERN.test(value)

export const hasValidTimeShape = (value: string): boolean =>
  VALID_TIME_PATTERN.test(value)

export const isWithinBounds = (
  value: string,
  min: string | undefined,
  max: string | undefined
): boolean => (!min || value >= min) && (!max || value <= max)

export const isTimeWithinBounds = (
  value: string,
  min: string | undefined,
  max: string | undefined
): boolean =>
  min && max && min > max
    ? value >= min || value <= max
    : isWithinBounds(value, min, max)

export const timeInMilliseconds = (value: string): number | null => {
  const match = TIME_PATTERN.exec(value)

  if (!match) {
    return null
  }

  const hour = Number(match[1])
  const minute = Number(match[2])
  const second = Number(match[3] ?? '0')
  const millisecond = Number((match[4] ?? '').padEnd(3, '0'))

  return hour < 24 && minute < 60 && second < 60
    ? ((hour * 60 + minute) * 60 + second) * 1000 + millisecond
    : null
}

export const isTimeStepAligned = (
  value: string,
  min: string | undefined,
  step: number | string
): boolean => {
  const valueInMilliseconds = timeInMilliseconds(value)
  const baseInMilliseconds = timeInMilliseconds(timePart(min)) ?? 0
  const stepInMilliseconds = Number(step) * 1000

  if (
    valueInMilliseconds === null ||
    !Number.isFinite(stepInMilliseconds) ||
    stepInMilliseconds <= 0
  ) {
    return valueInMilliseconds !== null
  }

  const steps = (valueInMilliseconds - baseInMilliseconds) / stepInMilliseconds
  return Math.abs(steps - Math.round(steps)) < 1e-7
}
